/**
 * Layout Component
 * Provides common layout with navigation
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';
import logger from '../utils/logger';

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isManager } = useAuth();
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    logger.info('User logging out', { userId: user?.id });
    await logout();
    navigate('/login');
  };
  
  const handleNavigation = (path) => {
    logger.debug('Navigation', { path, userId: user?.id });
    navigate(path);
  };
  
  const getRoleBadge = () => {
    if (user?.role === 'admin') {
      return <span className="px-2 py-1 text-xs font-semibold bg-primary-600 text-white rounded-full">Admin</span>;
    }
    if (user?.role === 'manager') {
      return <span className="px-2 py-1 text-xs font-semibold bg-primary-500 text-white rounded-full">Manager</span>;
    }
    return <span className="px-2 py-1 text-xs font-semibold bg-green-500 text-white rounded-full">Employee</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-primary-50 to-primary-100 flex flex-col">
      {/* Navigation Bar */}
      <nav className="bg-white/80 backdrop-blur-lg shadow-lg border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center space-x-8">
              {/* HID Global Logo and Branding */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <img 
                    src="/Ignite_Logo.png" 
                    alt="IDT IGNITE QUARKS" 
                    className="h-12 w-auto max-w-[200px]"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="h-12 w-px bg-gray-300"></div>
                  <div>
                    <h1 className="text-xl font-bold text-primary-500">
                      IDT IGNITE QUARKS
                    </h1>
                    <p className="text-xs text-gray-500">Innovation Token Distribution</p>
                  </div>
                </div>
              </div>
              <div className="hidden md:flex space-x-1">
                <button
                  onClick={() => handleNavigation('/dashboard')}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-all duration-200 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span>Dashboard</span>
                </button>
                {isManager() && (
                  <button
                    onClick={() => handleNavigation('/manager')}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-all duration-200 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span>Manager</span>
                  </button>
                )}
                {isAdmin() && (
                  <button
                    onClick={() => handleNavigation('/admin')}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-all duration-200 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Admin</span>
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden sm:flex items-center space-x-3 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-gray-800">{user?.name}</span>
                  {getRoleBadge()}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-rose-600 rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Main Content */}
      <main className="flex-1 pb-8">{children}</main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Layout;

